import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CarriageBucketJob,
  CarriageException,
  CarriagePostReceipt,
  Carrier,
  CourierInvoice,
  CourierInvoicePreview,
  ExceptionResolutionKind,
} from "@shared-core/data/carriage-cost-types";
import {
  CARRIER_LABELS,
  INVOICE_STATUS_LABELS,
  MATCHED_BY_LABELS,
  allocatedShare,
  formatMonthKey,
  splitSegments,
} from "@shared-core/domain/carriage-costs";
import { formatCurrencyGbp } from "@shared-core/domain/formatters";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../app/components/primitives/tabs";
import {
  EntityCombobox,
  type EntityOption,
} from "../app/components/patterns/EntityCombobox";
import { SlideOverPanel } from "../app/components/patterns/SlideOverPanel";
import { StatCard } from "../app/components/patterns/StatCard";
import { CheckCircle2, Truck, Upload } from "lucide-react";
import { toast } from "sonner";

// Commercial → Carriage: courier invoices in, carriage cost on jobs out.
// Three tabs map to three tasks — Invoices (upload, review the split, post),
// Exceptions (the standing queue of what the cascade could not place),
// Buckets (the per-customer monthly jobs and the catch-all). Matched rows
// post without review; only the residual is a queue.
// Design: docs/features/carriage-cost-capture/01_product_brief.md §8.

const money = (v: number) => formatCurrencyGbp(v, 2);
// A consignment's collection date is nullable: some carrier rows carry no
// parsable date, and the queue still has to show the row.
const gbDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const STATUS_TONE: Record<CourierInvoice["status"], string> = {
  parsed: "bg-muted text-muted-foreground",
  reviewed: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300",
  posted:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
  partially_posted:
    "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
};

const SEGMENT_TONE: Record<string, string> = {
  delivery_note: "bg-emerald-500",
  job_number: "bg-emerald-400",
  sales_order: "bg-teal-400",
  manual: "bg-blue-400",
  unallocated: "bg-amber-400",
};

function SplitBar({ invoice }: { invoice: CourierInvoice }) {
  const total = invoice.consignment_count || 1;
  return (
    <div className="space-y-1">
      <div className="bg-muted flex h-2 w-full overflow-hidden rounded">
        {splitSegments(invoice.split).map((s) =>
          s.count ? (
            <div
              key={s.key}
              className={SEGMENT_TONE[s.key]}
              style={{ width: `${(100 * s.count) / total}%` }}
              title={`${MATCHED_BY_LABELS[s.key]}: ${s.count} · ${money(s.value)}`}
            />
          ) : null,
        )}
      </div>
      <div className="text-muted-foreground flex flex-wrap gap-x-3 text-xs">
        {splitSegments(invoice.split)
          .filter((s) => s.count)
          .map((s) => (
            <span key={s.key}>
              <span
                className={`mr-1 inline-block h-2 w-2 rounded-sm ${SEGMENT_TONE[s.key]}`}
              />
              {MATCHED_BY_LABELS[s.key]} {s.count}
            </span>
          ))}
      </div>
    </div>
  );
}

// ── Invoices tab ────────────────────────────────────────────────────────────

type UploadStage =
  | { status: "idle" }
  | { status: "previewing" }
  | { status: "previewed"; preview: CourierInvoicePreview }
  | { status: "uploading"; preview: CourierInvoicePreview }
  | { status: "failed"; message: string };

function InvoicesTab({
  onExceptionsChanged,
}: {
  onExceptionsChanged: () => void;
}) {
  const adapter = useDataAdapter();
  const [invoices, setInvoices] = useState<CourierInvoice[] | null>(null);
  const [selected, setSelected] = useState<CourierInvoice | null>(null);
  const [posting, setPosting] = useState(false);
  const [receipt, setReceipt] = useState<CarriagePostReceipt | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [carrier, setCarrier] = useState<Carrier>("dx");
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<UploadStage>({ status: "idle" });
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const { invoices } = await adapter.listCourierInvoices();
    setInvoices(invoices);
  }, [adapter]);

  useEffect(() => {
    load();
  }, [load]);

  const runPreview = async () => {
    if (!file) return;
    setStage({ status: "previewing" });
    try {
      const preview = await adapter.previewCourierInvoice({ file, carrier });
      setStage({ status: "previewed", preview });
    } catch {
      setStage({
        status: "failed",
        message: "The file could not be read as a courier invoice.",
      });
    }
  };

  const runUpload = async (preview: CourierInvoicePreview) => {
    if (!file) return;
    setStage({ status: "uploading", preview });
    try {
      const invoice = await adapter.uploadCourierInvoice({ file, carrier });
      toast.success(
        `Invoice ${invoice.invoice_no} saved — ${invoice.consignment_count} consignments`,
      );
      setUploadOpen(false);
      setFile(null);
      setStage({ status: "idle" });
      if (fileInput.current) fileInput.current.value = "";
      await load();
      setSelected(invoice);
      onExceptionsChanged();
    } catch {
      setStage({
        status: "failed",
        message: "The invoice could not be saved. Nothing was posted.",
      });
    }
  };

  const runPost = async (invoice: CourierInvoice) => {
    setPosting(true);
    try {
      const r = await adapter.postCourierInvoice(invoice.id);
      setReceipt(r);
      toast.success(
        `Posted ${r.posted} carriage costs (${money(r.posted_value)}) to Tharstern`,
      );
      await load();
      setSelected(
        (await adapter.listCourierInvoices()).invoices.find(
          (i) => i.id === invoice.id,
        ) ?? null,
      );
    } catch {
      toast.error(
        "Posting failed. Nothing was double-posted; re-run when ready.",
      );
    } finally {
      setPosting(false);
    }
  };

  const busy = stage.status === "previewing" || stage.status === "uploading";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          Matched consignments post to Tharstern as carriage cost on the job.
          Re-uploading an invoice is a no-op.
        </p>
        <Button onClick={() => setUploadOpen(true)}>
          <Upload className="mr-2 h-4 w-4" /> Upload invoice
        </Button>
      </div>

      <div className="border-border bg-surface overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice</TableHead>
              <TableHead>Carrier</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Consignments</TableHead>
              <TableHead className="text-right">Nett</TableHead>
              <TableHead className="text-right">Matched</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices === null && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-muted-foreground text-center text-sm"
                >
                  Loading invoices…
                </TableCell>
              </TableRow>
            )}
            {invoices?.map((inv) => {
              const share = allocatedShare(inv.split);
              return (
                <TableRow
                  key={inv.id}
                  className="cursor-pointer"
                  onClick={() => {
                    setReceipt(null);
                    setSelected(inv);
                  }}
                >
                  <TableCell className="font-medium">
                    {inv.invoice_no}
                  </TableCell>
                  <TableCell>{CARRIER_LABELS[inv.carrier]}</TableCell>
                  <TableCell>{gbDate(inv.invoice_date)}</TableCell>
                  <TableCell className="text-right">
                    {inv.consignment_count}
                  </TableCell>
                  <TableCell className="text-right">
                    {money(inv.nett)}
                    {!inv.reconciles && (
                      <span
                        className="text-amber-700 ml-1 text-xs"
                        title="Parsed charges do not equal the stated nett"
                      >
                        ≠
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {share.countPct.toFixed(0)}%
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={STATUS_TONE[inv.status]}
                      variant="secondary"
                    >
                      {INVOICE_STATUS_LABELS[inv.status]}
                    </Badge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <SlideOverPanel
        open={selected !== null}
        onOpenChange={(o) => {
          if (!o) {
            setSelected(null);
            setReceipt(null);
          }
        }}
        title={
          selected
            ? `${CARRIER_LABELS[selected.carrier]} invoice ${selected.invoice_no}`
            : ""
        }
        description={
          selected
            ? `${gbDate(selected.period_from)} – ${gbDate(selected.period_to)} · uploaded ${gbDate(selected.uploaded_at)} by ${selected.uploaded_by}`
            : undefined
        }
        footer={
          selected && (
            <div className="flex w-full items-center justify-between gap-3">
              <span className="text-muted-foreground text-sm">
                {selected.split.unallocated.count > 0
                  ? `${selected.split.unallocated.count} exceptions (${money(selected.split.unallocated.value)}) can be resolved now or later`
                  : "Everything on this invoice is allocated"}
              </span>
              <Button
                onClick={() => runPost(selected)}
                disabled={
                  posting ||
                  allocatedShare(selected.split).count === selected.posted_lines
                }
              >
                {posting
                  ? "Posting…"
                  : selected.posted_lines > 0
                    ? `Post ${allocatedShare(selected.split).count - selected.posted_lines} new to Tharstern`
                    : `Post ${allocatedShare(selected.split).count} to Tharstern`}
              </Button>
            </div>
          )
        }
      >
        {selected && (
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-3">
              <StatCard
                label="Consignments"
                value={selected.consignment_count}
              />
              <StatCard
                label="Charges"
                value={money(selected.consignment_charges)}
              />
              <StatCard label="Fuel & levies" value={money(selected.levies)} />
            </div>
            <div>
              <div className="mb-1 flex items-baseline justify-between">
                <span className="text-sm font-medium">Allocation</span>
                <span className="text-muted-foreground text-xs">
                  {allocatedShare(selected.split).count} of{" "}
                  {selected.consignment_count} ·{" "}
                  {money(allocatedShare(selected.split).value)} of{" "}
                  {money(selected.nett)}
                </span>
              </div>
              <SplitBar invoice={selected} />
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {splitSegments(selected.split).map((s) => (
                <div key={s.key} className="contents">
                  <dt className="text-muted-foreground">
                    {MATCHED_BY_LABELS[s.key]}
                  </dt>
                  <dd className="text-right">
                    {s.count} · {money(s.value)}
                  </dd>
                </div>
              ))}
              <dt className="text-muted-foreground border-border border-t pt-2">
                Posted to Tharstern
              </dt>
              <dd className="border-border border-t pt-2 text-right">
                {selected.posted_lines > 0
                  ? `${selected.posted_lines} lines · ${selected.posted_at ? gbDate(selected.posted_at) : ""}`
                  : "Not yet"}
              </dd>
            </dl>
            {receipt && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm dark:border-emerald-900 dark:bg-emerald-950">
                <div className="flex items-center gap-2 font-medium">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Posted{" "}
                  {gbDate(receipt.posted_at)}
                </div>
                <ul className="text-muted-foreground mt-1 space-y-0.5">
                  <li>
                    {receipt.posted} carriage costs posted ·{" "}
                    {money(receipt.posted_value)}
                  </li>
                  {receipt.skipped_already_posted > 0 && (
                    <li>
                      {receipt.skipped_already_posted} already posted, skipped
                    </li>
                  )}
                  {receipt.failed > 0 && (
                    <li className="text-red-700">
                      {receipt.failed} failed — re-run to retry
                    </li>
                  )}
                  <li>
                    {receipt.exceptions_outstanding} exceptions still to resolve
                  </li>
                </ul>
              </div>
            )}
          </div>
        )}
      </SlideOverPanel>

      <SlideOverPanel
        open={uploadOpen}
        onOpenChange={(o) => {
          if (!busy) setUploadOpen(o);
        }}
        title="Upload courier invoice"
        description="Nothing is posted until you press Post on the invoice."
        footer={
          stage.status === "previewed" || stage.status === "uploading" ? (
            <div className="flex w-full justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setStage({ status: "idle" })}
                disabled={busy}
              >
                Back
              </Button>
              <Button
                onClick={() =>
                  stage.status === "previewed" && runUpload(stage.preview)
                }
                disabled={busy}
              >
                {stage.status === "uploading" ? "Saving…" : "Save invoice"}
              </Button>
            </div>
          ) : (
            <div className="flex w-full justify-end">
              <Button onClick={runPreview} disabled={!file || busy}>
                {stage.status === "previewing" ? "Reading…" : "Check file"}
              </Button>
            </div>
          )
        }
      >
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="carriage-carrier">
              Carrier
            </label>
            <Select
              value={carrier}
              onValueChange={(v) => setCarrier(v as Carrier)}
            >
              <SelectTrigger id="carriage-carrier" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dx">DX (.xls export)</SelectItem>
                <SelectItem value="dpd">DPD (.csv invoice data)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="carriage-file">
              Invoice file
            </label>
            <input
              id="carriage-file"
              ref={fileInput}
              type="file"
              accept={carrier === "dx" ? ".xls" : ".csv"}
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setStage({ status: "idle" });
              }}
              className="text-sm"
            />
            <p className="text-muted-foreground text-xs">
              The file the carrier emails with the invoice, unchanged.
            </p>
          </div>

          {stage.status === "failed" && (
            <p className="text-sm text-red-700">{stage.message}</p>
          )}

          {(stage.status === "previewed" || stage.status === "uploading") && (
            <div className="border-border space-y-3 rounded-md border p-3 text-sm">
              <div className="flex items-baseline justify-between">
                <span className="font-medium">
                  {CARRIER_LABELS[stage.preview.carrier]}{" "}
                  {stage.preview.invoice_no}
                </span>
                <span className="text-muted-foreground">
                  {gbDate(stage.preview.invoice_date)}
                </span>
              </div>
              {stage.preview.already_uploaded && (
                <p className="text-amber-700">
                  This invoice number is already uploaded. Saving again changes
                  nothing.
                </p>
              )}
              <dl className="grid grid-cols-2 gap-y-1">
                <dt className="text-muted-foreground">Consignments</dt>
                <dd className="text-right">
                  {stage.preview.consignment_count}
                </dd>
                <dt className="text-muted-foreground">Charges + levies</dt>
                <dd className="text-right">
                  {money(stage.preview.consignment_charges)} +{" "}
                  {money(stage.preview.levies)}
                </dd>
                <dt className="text-muted-foreground">Nett</dt>
                <dd className="text-right">
                  {money(stage.preview.nett)}{" "}
                  {stage.preview.reconciles ? (
                    <span className="text-emerald-700">= stated</span>
                  ) : (
                    <span className="text-amber-700">
                      ≠ stated {money(stage.preview.stated_nett)}
                    </span>
                  )}
                </dd>
                <dt className="text-muted-foreground">Will match</dt>
                <dd className="text-right">
                  {stage.preview.consignment_count -
                    stage.preview.split.unallocated.count}{" "}
                  · {stage.preview.split.unallocated.count} to review
                </dd>
              </dl>
            </div>
          )}
        </div>
      </SlideOverPanel>
    </div>
  );
}

// ── Exceptions tab ──────────────────────────────────────────────────────────

function ExceptionsTab({
  refreshKey,
  onResolved,
}: {
  refreshKey: number;
  onResolved: () => void;
}) {
  const adapter = useDataAdapter();
  const [rows, setRows] = useState<CarriageException[] | null>(null);
  const [total, setTotal] = useState(0);
  const [carrier, setCarrier] = useState<Carrier | "all">("all");
  const [q, setQ] = useState("");
  const [active, setActive] = useState<CarriageException | null>(null);
  const [kind, setKind] = useState<ExceptionResolutionKind>("job");
  const [job, setJob] = useState<EntityOption | null>(null);
  const [customer, setCustomer] = useState<EntityOption | null>(null);
  const [remember, setRemember] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await adapter.listCarriageExceptions({
      carrier: carrier === "all" ? undefined : carrier,
      q: q || undefined,
      pageSize: 50,
    });
    setRows(res.items);
    setTotal(res.total);
  }, [adapter, carrier, q]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const open = (ex: CarriageException) => {
    setActive(ex);
    const s = ex.suggestion;
    const knownCustomer = s.customer_id
      ? { id: s.customer_id, label: s.customer_name ?? s.customer_id }
      : null;
    const preferred =
      ex.remembered_job_no ?? (s.kind === "job" ? s.job_no : null);
    setCustomer(knownCustomer);
    if (preferred) {
      setKind("job");
      setJob({
        id: preferred,
        label: s.kind === "job" && s.job_no === preferred ? s.label : preferred,
      });
    } else if (s.kind === "bucket") {
      setKind(s.label.includes("catch-all") ? "catch_all" : "customer_bucket");
      setJob(null);
    } else {
      setKind("job");
      setJob(null);
    }
    setRemember(Boolean(ex.customer_reference));
  };

  // Job lookup is customer-scoped on the platform, so the operator names the
  // customer (pre-filled from the consignee where the system knows it) and
  // then the job within it. Two clicks, no cross-customer job typing.
  const fetchJobs = useCallback(
    async (query: string): Promise<EntityOption[]> => {
      if (!customer) return [];
      const res = await adapter.getJobsForIssuePicker(customer.id, {
        q: query,
        pageSize: 8,
      });
      return res.items.map((j) => ({
        id: j.job_no,
        label: `${j.job_no} · ${j.title}`,
      }));
    },
    [adapter, customer],
  );
  const fetchCustomers = useCallback(
    async (query: string): Promise<EntityOption[]> => {
      const res = await adapter.getCustomersForIssuePicker({
        q: query,
        pageSize: 8,
      });
      return res.items.map((c) => ({ id: c.id, label: c.name }));
    },
    [adapter],
  );

  const canSave =
    kind === "job"
      ? job !== null && customer !== null
      : kind === "customer_bucket"
        ? customer !== null
        : true;

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await adapter.resolveCarriageException(active.id, {
        kind,
        job_no: kind === "job" ? job?.id : undefined,
        customer_id: kind === "customer_bucket" ? customer?.id : undefined,
        remember: kind === "job" && remember,
      });
      toast.success(`${money(active.cost)} allocated`);
      setActive(null);
      await load();
      onResolved();
    } catch {
      toast.error("Could not resolve this exception.");
    } finally {
      setSaving(false);
    }
  };

  const totalValue = useMemo(
    () => (rows ?? []).reduce((v, r) => v + r.cost, 0),
    [rows],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={carrier}
          onValueChange={(v) => setCarrier(v as Carrier | "all")}
        >
          <SelectTrigger className="w-36" aria-label="Carrier">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All carriers</SelectItem>
            <SelectItem value="dx">DX</SelectItem>
            <SelectItem value="dpd">DPD</SelectItem>
          </SelectContent>
        </Select>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search reference, consignee, postcode…"
          className="w-72"
          aria-label="Search exceptions"
        />
        <span className="text-muted-foreground ml-auto text-sm">
          {total} to resolve · {money(totalValue)}
        </span>
      </div>

      <div className="border-border bg-surface overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reference as typed</TableHead>
              <TableHead>Consignee</TableHead>
              <TableHead>Postcode</TableHead>
              <TableHead>Collected</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead>Best guess</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-muted-foreground text-center text-sm"
                >
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {rows?.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-muted-foreground text-center text-sm"
                >
                  Nothing to resolve. Every consignment on every uploaded
                  invoice has a job.
                </TableCell>
              </TableRow>
            )}
            {rows?.map((ex) => (
              <TableRow
                key={ex.id}
                className="cursor-pointer"
                onClick={() => open(ex)}
              >
                <TableCell className="font-mono text-sm">
                  {ex.customer_reference || (
                    <span className="text-muted-foreground italic">blank</span>
                  )}
                </TableCell>
                <TableCell>{ex.consignee}</TableCell>
                <TableCell>{ex.delivery_postcode}</TableCell>
                <TableCell>{gbDate(ex.collected_on)}</TableCell>
                <TableCell className="text-right font-medium">
                  {money(ex.cost)}
                </TableCell>
                <TableCell className="text-sm">
                  {ex.remembered_job_no ? (
                    <span>
                      <Badge variant="secondary" className="mr-1">
                        remembered
                      </Badge>
                      {ex.remembered_job_no}
                    </span>
                  ) : ex.suggestion.kind === "none" ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    ex.suggestion.label
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <SlideOverPanel
        open={active !== null}
        onOpenChange={(o) => !o && setActive(null)}
        title={active ? `Consignment ${active.consignment_no}` : ""}
        description={
          active
            ? `${CARRIER_LABELS[active.carrier]} ${active.invoice_no} · ${active.service} · ${active.parcels} parcel${active.parcels === 1 ? "" : "s"} · ${money(active.cost)}`
            : undefined
        }
        footer={
          <div className="flex w-full justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setActive(null)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={save} disabled={!canSave || saving}>
              {saving ? "Saving…" : "Allocate"}
            </Button>
          </div>
        }
      >
        {active && (
          <div className="space-y-5 text-sm">
            <dl className="grid grid-cols-3 gap-y-1">
              <dt className="text-muted-foreground">Reference</dt>
              <dd className="col-span-2 font-mono">
                {active.customer_reference || "(blank)"}
              </dd>
              <dt className="text-muted-foreground">Consignee</dt>
              <dd className="col-span-2">
                {active.consignee} · {active.delivery_postcode}
              </dd>
              <dt className="text-muted-foreground">Collected</dt>
              <dd className="col-span-2">{gbDate(active.collected_on)}</dd>
            </dl>
            {active.suggestion.kind !== "none" && (
              <p className="text-muted-foreground rounded-md border border-dashed p-2 text-xs">
                Suggested:{" "}
                <span className="text-foreground">
                  {active.suggestion.label}
                </span>{" "}
                — {active.suggestion.reason}
              </p>
            )}

            <div className="space-y-3">
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="kind"
                  checked={kind === "job"}
                  onChange={() => setKind("job")}
                  className="mt-1"
                />
                <span className="flex-1 space-y-2">
                  <span className="font-medium">Assign to a job</span>
                  {kind === "job" && (
                    <>
                      <EntityCombobox
                        value={customer}
                        onChange={(c) => {
                          setCustomer(c);
                          setJob(null);
                        }}
                        fetchOptions={fetchCustomers}
                        placeholder="Customer…"
                        ariaLabel="Customer"
                        clearable
                      />
                      <EntityCombobox
                        value={job}
                        onChange={setJob}
                        fetchOptions={fetchJobs}
                        placeholder={
                          customer
                            ? "Job number or description…"
                            : "Pick the customer first"
                        }
                        ariaLabel="Job"
                        disabled={!customer}
                        clearable
                      />
                    </>
                  )}
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="kind"
                  checked={kind === "customer_bucket"}
                  onChange={() => setKind("customer_bucket")}
                  className="mt-1"
                />
                <span className="flex-1 space-y-2">
                  <span className="font-medium">
                    Customer&rsquo;s monthly carriage job
                  </span>
                  <span className="text-muted-foreground block text-xs">
                    For stock calloffs with no job of their own.
                  </span>
                  {kind === "customer_bucket" && (
                    <EntityCombobox
                      value={customer}
                      onChange={setCustomer}
                      fetchOptions={fetchCustomers}
                      placeholder="Customer…"
                      ariaLabel="Customer"
                      clearable
                    />
                  )}
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="kind"
                  checked={kind === "catch_all"}
                  onChange={() => setKind("catch_all")}
                  className="mt-1"
                />
                <span className="flex-1">
                  <span className="font-medium">
                    This month&rsquo;s catch-all
                  </span>
                  <span className="text-muted-foreground block text-xs">
                    Unattributable spend. Kept visible so it stays small.
                  </span>
                </span>
              </label>
            </div>

            {kind === "job" && active.customer_reference && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                Offer this job next time the reference reads &ldquo;
                {active.customer_reference}&rdquo;
              </label>
            )}
          </div>
        )}
      </SlideOverPanel>
    </div>
  );
}

// ── Buckets tab ─────────────────────────────────────────────────────────────

function BucketsTab({ refreshKey }: { refreshKey: number }) {
  const adapter = useDataAdapter();
  const [month, setMonth] = useState("2026-08");
  const [buckets, setBuckets] = useState<CarriageBucketJob[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [customer, setCustomer] = useState<EntityOption | null>(null);
  const [isCatchAll, setIsCatchAll] = useState(false);
  const [jobNo, setJobNo] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { buckets } = await adapter.listCarriageBucketJobs(month);
    setBuckets(buckets);
  }, [adapter, month]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const fetchCustomers = useCallback(
    async (query: string): Promise<EntityOption[]> => {
      const res = await adapter.getCustomersForIssuePicker({
        q: query,
        pageSize: 8,
      });
      return res.items.map((c) => ({ id: c.id, label: c.name }));
    },
    [adapter],
  );

  const save = async () => {
    setSaving(true);
    try {
      await adapter.registerCarriageBucketJob({
        month,
        customer_id: isCatchAll ? null : (customer?.id ?? null),
        job_no: jobNo.trim(),
      });
      toast.success("Bucket job registered");
      setAdding(false);
      setCustomer(null);
      setJobNo("");
      setIsCatchAll(false);
      await load();
    } catch {
      toast.error("Could not register the bucket job.");
    } finally {
      setSaving(false);
    }
  };

  const catchAll = buckets?.find((b) => b.customer_id === null);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-40" aria-label="Month">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {["2026-09", "2026-08", "2026-07"].map((m) => (
              <SelectItem key={m} value={m}>
                {formatMonthKey(m)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-sm">
          A bucket is a real Tharstern job that carries the month&rsquo;s
          carriage cost for a customer&rsquo;s stock calloffs. Raise it in
          Tharstern, register it here.
        </p>
        <Button
          className="ml-auto"
          variant="outline"
          onClick={() => setAdding(true)}
        >
          Register bucket job
        </Button>
      </div>

      {!catchAll && buckets && (
        <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
          No catch-all job registered for {formatMonthKey(month)}. Exceptions
          cannot be sent to the catch-all until one is.
        </p>
      )}

      <div className="border-border bg-surface overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Bucket job</TableHead>
              <TableHead className="text-right">Consignments</TableHead>
              <TableHead className="text-right">Carriage cost</TableHead>
              <TableHead>Registered by</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {buckets?.map((b) => (
              <TableRow key={b.id}>
                <TableCell className="font-medium">
                  {b.customer_name ?? (
                    <span className="text-muted-foreground italic">
                      Catch-all
                    </span>
                  )}
                </TableCell>
                <TableCell className="font-mono">{b.job_no}</TableCell>
                <TableCell className="text-right">
                  {b.allocated_count}
                </TableCell>
                <TableCell className="text-right">
                  {money(b.allocated_value)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {b.registered_by}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <SlideOverPanel
        open={adding}
        onOpenChange={setAdding}
        title={`Register bucket job — ${formatMonthKey(month)}`}
        footer={
          <div className="flex w-full justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setAdding(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving || !jobNo.trim() || (!isCatchAll && !customer)}
            >
              {saving ? "Saving…" : "Register"}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isCatchAll}
              onChange={(e) => setIsCatchAll(e.target.checked)}
            />
            This is the month&rsquo;s catch-all
          </label>
          {!isCatchAll && (
            <div className="space-y-1">
              <span className="font-medium">Customer</span>
              <EntityCombobox
                value={customer}
                onChange={setCustomer}
                fetchOptions={fetchCustomers}
                placeholder="Customer…"
                ariaLabel="Customer"
                clearable
              />
            </div>
          )}
          <div className="space-y-1">
            <label className="font-medium" htmlFor="bucket-job">
              Tharstern job number
            </label>
            <Input
              id="bucket-job"
              value={jobNo}
              onChange={(e) => setJobNo(e.target.value)}
              placeholder="e.g. 737120"
              className="w-48 font-mono"
            />
          </div>
        </div>
      </SlideOverPanel>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export function CarriageCostsPage() {
  const [exceptionsKey, setExceptionsKey] = useState(0);
  const bump = () => setExceptionsKey((k) => k + 1);
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-foreground mb-2 flex items-center gap-2">
          <Truck className="h-6 w-6" /> Carriage
        </h1>
        <p className="text-muted-foreground">
          Courier invoices in, carriage cost on the right job in Tharstern. What
          the matching cannot place waits here until someone places it.
        </p>
      </div>
      <Tabs defaultValue="invoices" className="gap-6">
        <TabsList variant="underline">
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="exceptions">Exceptions</TabsTrigger>
          <TabsTrigger value="buckets">Buckets</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices">
          <InvoicesTab onExceptionsChanged={bump} />
        </TabsContent>
        <TabsContent value="exceptions">
          <ExceptionsTab refreshKey={exceptionsKey} onResolved={bump} />
        </TabsContent>
        <TabsContent value="buckets">
          <BucketsTab refreshKey={exceptionsKey} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
